import React from 'react';
import { Await, Link } from 'react-router';

import { makeApiRequestHeaders } from '~/api/headers';
import { isLoggedIn } from '~/api/is-logged-in';
import { Type, surfaceNewsV1 } from '~/api/v1/mall/surface-news';
import { surfaceV1 } from '~/api/v1/mall/surface/surface-name';
import { campaignSearchV2 } from '~/api/v2/mall/campaign/search';
import { commonDataV2Cached } from '~/api/v2/mall/common-data';
import { recommendShopV2 } from '~/api/v2/mall/recommend-shop';
import { segmentV2 } from '~/api/v2/mall/segment';
import { CampaignTypeV2 } from '~/api/v2/schemas/campaign-type';
import { RewardTypeV2 } from '~/api/v2/schemas/reward-type';
import { Category } from '~/features/category';
import { AwaitErrorElement } from '~/ui/await-error-element';
import { isDeviceMobile, isDeviceMobileOrTablet } from '~/utils';

import { SiteGlobalMenu } from '../components/site-global-menu';
import { SiteMainContents } from '../components/site-main-contents';
import { SiteShopRanking } from '../components/site-shop-ranking';
import { categoryIcons } from '../config';
import { categoryStyles } from '../styles';
import { metaTitle } from '../utils/meta-title';
import type { Route } from './+types/service';

export const meta: Route.MetaFunction = () => {
  return [{ title: metaTitle('登録や申込TOP') }];
};
export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const path = url.pathname;
  const isMobileOrTablet = isDeviceMobileOrTablet(request.headers.get('user-agent'));
  const isMobile = isDeviceMobile(request.headers.get('user-agent'));

  const headers = await makeApiRequestHeaders(request);
  const loggedIn = await isLoggedIn(request);

  // Promiseとして返すAPI（<Await>で処理）
  const promises = {
    recommendShopPromise: recommendShopV2(headers, { limit: 8 }),
    importantNewsPromise: surfaceNewsV1(headers, { type: Type.IMPORTANT }),
    shopRankingPromise: campaignSearchV2(headers, {
      campaign_type: CampaignTypeV2.SHOP,
      reward_type: RewardTypeV2.AMOUNT,
      limit: isMobile ? 10 : 20,
    }),
    commonDataPromise: commonDataV2Cached(headers),
    segmentPromise: loggedIn ? segmentV2(headers) : undefined,
    topSlideBannerPromise: surfaceV1(headers, 'top_slide_banner'),
  };

  return {
    path,
    isMobileOrTablet,
    ...promises,
  };
};

export default function Service({
  loaderData: {
    recommendShopPromise,
    importantNewsPromise,
    shopRankingPromise,
    commonDataPromise,
    segmentPromise,
    topSlideBannerPromise,
    path,
    isMobileOrTablet,
  },
}: Route.ComponentProps) {
  return (
    <>
      <div className="mb-16">
        <SiteMainContents
          topSlideBannerPromise={topSlideBannerPromise}
          recommendShopPromise={recommendShopPromise}
          importantNewsPromise={importantNewsPromise}
          path={path}
        />

        <div className="lg:main-container mt-4 mb-16 md:mt-14">
          <SiteGlobalMenu isMobileOrTablet={isMobileOrTablet} />
        </div>
      </div>

      <div className="main-container flex flex-col gap-16 md:gap-30">
        {/* Display shop ranking section only when:
         - Both promises resolve successfully
         - Campaign info exists (shopRanking.campaign_info)
         - At least one campaign exists (shopRanking.campaign_info.length > 0)
         ショップランキングセクションの表示条件:
         - 両方のPromiseが正常に解決された場合
         - キャンペーン情報が存在する場合（shopRanking.campaign_info）
         - 少なくとも1つのキャンペーンが存在する場合（shopRanking.campaign_info.length > 0）*/}
        {commonDataPromise && shopRankingPromise && (
          <React.Suspense fallback={null}>
            <Await resolve={commonDataPromise} errorElement={<AwaitErrorElement message="commonData Promise failed" />}>
              {(commonData) => (
                <Await
                  resolve={shopRankingPromise}
                  errorElement={<AwaitErrorElement message="shopRanking Promise failed" />}>
                  {(shopRanking) =>
                    shopRanking.campaign_info &&
                    shopRanking.campaign_info.length > 0 && (
                      <div>
                        <div className="mb-6 flex items-center justify-between">
                          <h2 className="font-palt text-headline-xs">人気の登録や申し込み</h2>
                          <Link
                            to={`${import.meta.env.VITE_CAMPAIGN_SEARCH_ROUTE}?campaign_type=1&reward_type=2`}
                            className="text-body-s text-blue-500 hover:underline"
                            aria-label="人気のお店すべて見る">
                            すべて見る
                          </Link>
                        </div>

                        <SiteShopRanking
                          commonData={commonData}
                          campaignSearch={shopRanking}
                          segment={segmentPromise}
                        />
                      </div>
                    )
                  }
                </Await>
              )}
            </Await>
          </React.Suspense>
        )}

        {/* Display category section only when:
         - Promise resolves successfully
         - Response contains categories (commonData.categories)
         カテゴリーセクションの表示条件:
         - Promiseが正常に解決された場合
         - レスポンスにカテゴリーが含まれる場合（commonData.categories）*/}
        {commonDataPromise && (
          <React.Suspense fallback={null}>
            <Await resolve={commonDataPromise} errorElement={<AwaitErrorElement message="commonData Promise failed" />}>
              {(commonData) =>
                commonData.categories && (
                  <div>
                    <h2 className="text-headline-xs font-palt mb-6">カテゴリーから探す</h2>
                    <Category
                      styles={categoryStyles}
                      categories={commonData.categories}
                      categoryIcons={categoryIcons}
                      rewardType={RewardTypeV2.AMOUNT}
                    />
                  </div>
                )
              }
            </Await>
          </React.Suspense>
        )}
      </div>
    </>
  );
}
